import { useState, useRef, useEffect } from 'react';
import { X, Send, Bot, User, Zap, Clock, Brain } from 'lucide-react';

const AIChatbot = ({ isOpen, onClose, initialPrompt = '' }) => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hello! I'm your AI assistant powered by LightRAG. Ask me anything about Jason's workflows, projects, and capabilities.",
      isBot: true,
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [queryMode, setQueryMode] = useState('naive'); // 'naive' for fast, 'global' for deep, 'hybrid' for comprehensive
  const messagesEndRef = useRef(null);
  const latestBotMessageRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToLatestBotMessage = () => {
    latestBotMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    // If the latest message is from the bot, scroll to the start of that message
    // Otherwise, scroll to bottom (for user messages)
    const latestMessage = messages[messages.length - 1];
    if (latestMessage && latestMessage.isBot && messages.length > 1) {
      // Small delay to ensure the message is rendered
      setTimeout(() => {
        scrollToLatestBotMessage();
      }, 100);
    } else {
      scrollToBottom();
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Lock body scroll when chatbot is open
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      
      // Lock scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      return () => {
        // Restore scroll
        const savedScrollY = document.body.style.top;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, parseInt(savedScrollY || '0') * -1);
      };
    }
  }, [isOpen]);

  // Populate input with initial prompt when opened
  useEffect(() => {
    if (isOpen && initialPrompt) {
      setInputValue(initialPrompt);
      // Focus on the input after setting the value
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // Move cursor to end of text
          inputRef.current.setSelectionRange(initialPrompt.length, initialPrompt.length);
        }
      }, 100);
    }
  }, [isOpen, initialPrompt]);


  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      text: inputValue,
      isBot: false,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Build conversation history from current messages (excluding the initial greeting)
      const conversationHistory = messages
        .filter(msg => msg.id !== 1) // Exclude the initial greeting
        .map(msg => ({
          role: msg.isBot ? 'assistant' : 'user',
          content: msg.text
        }));

      // Define parameters based on mode
      const modeParams = {
        naive: {
          top_k: 7,
          max_token_for_text_unit: 16000,
          max_token_for_global_context: 32000,
          max_token_for_local_context: 32000
        },
        global: {
          top_k: 12,
          max_token_for_text_unit: 24000,
          max_token_for_global_context: 64000,
          max_token_for_local_context: 64000
        },
        hybrid: {
          top_k: 20,
          max_token_for_text_unit: 32000,
          max_token_for_global_context: 128000,
          max_token_for_local_context: 128000
        }
      };

      const currentParams = modeParams[queryMode];

      // Generate unique IDs for tracking
      const sessionId = sessionStorage.getItem('chatSessionId') || Date.now().toString();
      if (!sessionStorage.getItem('chatSessionId')) {
        sessionStorage.setItem('chatSessionId', sessionId);
      }
      const requestId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

      const response = await fetch('/api/lightrag-proxy', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Session-ID': sessionId,
          'X-Request-ID': requestId
        },
        body: JSON.stringify({
          query: inputValue,
          mode: queryMode,
          only_need_context: false,
          only_need_prompt: false,
          response_type: "Multiple Paragraphs",
          top_k: currentParams.top_k,
          max_token_for_text_unit: currentParams.max_token_for_text_unit,
          max_token_for_global_context: currentParams.max_token_for_global_context,
          max_token_for_local_context: currentParams.max_token_for_local_context,
          conversation_history: conversationHistory,
          history_turns: conversationHistory.length
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Handle rate limiting specifically
        if (response.status === 429) {
          const retryAfter = errorData.retryAfter || response.headers.get('Retry-After') || 60;
          throw new Error(`Rate limit exceeded. Please try again in ${retryAfter} seconds.`);
        }
        
        throw new Error(`API Error ${response.status}: ${errorData.error || errorData.detail || errorData.message || 'Request failed'}`);
      }

      const data = await response.json();
      
      // Parse the LightRAG response
      let responseText = data.response || data.message || 'Sorry, I encountered an error processing your request.';
      
      // Handle the case where response is a tuple/array with text and metadata
      if (typeof responseText === 'string') {
        // Remove various leading patterns that can appear
        responseText = responseText.replace(/^(\("|\('|"|')/, '');
        
        // Extract the text content from the tuple format
        const match = responseText.match(/^(.+?)",?\s*\{.*\}\)$/s);
        if (match) {
          responseText = match[1];
        }
        
        // Also handle case where it ends with just the closing quote
        if (responseText.endsWith('"')) {
          responseText = responseText.slice(0, -1);
        }
      }
      
      // Convert escaped newlines to actual line breaks
      responseText = responseText.replace(/\\n/g, '\n');
      
      // Remove everything after Intelligence Sources section including any closing text
      const intelligenceSourcesIndex = responseText.indexOf('Intelligence Sources');
      if (intelligenceSourcesIndex !== -1) {
        // Find where the Intelligence Sources section actually ends
        const afterIntelligenceSources = responseText.substring(intelligenceSourcesIndex);
        
        // Look for the end of the file list (last .md file) and any trailing content
        const fileListMatch = afterIntelligenceSources.match(/- \[.*?\] .*?\.md\s*/g);
        if (fileListMatch) {
          // Find the end of the last file entry
          const lastFileMatch = fileListMatch[fileListMatch.length - 1];
          const lastFileIndex = afterIntelligenceSources.lastIndexOf(lastFileMatch);
          if (lastFileIndex !== -1) {
            const endOfLastFile = lastFileIndex + lastFileMatch.length;
            responseText = responseText.substring(0, intelligenceSourcesIndex + endOfLastFile);
          }
        } else {
          // Fallback: look for metadata patterns
          const endPatterns = [
            /',\s*\{/,   // Start of metadata tuple
            /\n\n---\n\n.*report/i,  // Report closing section
            /\n\n.*consolidates.*intelligence/i  // Closing summary line
          ];
          
          let cutIndex = responseText.length;
          for (const pattern of endPatterns) {
            const match = afterIntelligenceSources.match(pattern);
            if (match) {
              cutIndex = Math.min(cutIndex, intelligenceSourcesIndex + match.index);
              break;
            }
          }
          responseText = responseText.substring(0, cutIndex);
        }
      }
      
      // Remove any remaining metadata patterns
      const metadataPatterns = [
        /',\s*\{.*$/s,                // "', {metadata}"
        /'\)\s*$/s,                   // "')" at the end
        /,\s*\{.*$/s                   // ", {metadata}"
      ];
      
      for (const pattern of metadataPatterns) {
        responseText = responseText.replace(pattern, '');
      }
      
      // Clean up any trailing quotes, commas, or whitespace
      responseText = responseText.replace(/[',\s]*$/, '').trim();
      
      const botMessage = {
        id: Date.now() + 1,
        text: responseText.trim(),
        isBot: true,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        id: Date.now() + 1,
        text: `Error: ${error.message || 'Sorry, I\'m having trouble connecting right now. Please try again.'}`,
        isBot: true,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([
      {
        id: 1,
        text: "Hello! I'm your AI assistant powered by LightRAG. Ask me anything about Jason's workflows, projects, and capabilities.",
        isBot: true,
        timestamp: new Date()
      }
    ]);
  };

  // Format markdown content for display
  const formatMarkdownContent = (text) => {
    if (!text) return text;
    
    // Fix escaped apostrophes
    let formattedText = text.replace(/\\'/g, "'");
    
    // Remove "Tactical Report: " prefix from anywhere in headers
    formattedText = formattedText.replace(/^#\s*Tactical Report:\s*/gm, '# ');
    formattedText = formattedText.replace(/^##\s*Tactical Report:\s*/gm, '## ');
    formattedText = formattedText.replace(/^###\s*Tactical Report:\s*/gm, '### ');
    
    // Split into lines and process each line
    const lines = formattedText.split('\n');
    const formattedLines = lines.map((line, index) => {
      // Handle headers
      if (line.startsWith('### ')) {
        return (
          <h3 key={index} className="mb-2 mt-4 break-words text-lg font-semibold text-white" style={{ fontSize: '1.125rem' }}>
            {line.substring(4)}
          </h3>
        );
      } else if (line.startsWith('## ')) {
        return (
          <h2 key={index} className="mb-2 mt-4 break-words text-xl font-semibold text-white" style={{ fontSize: '1.25rem' }}>
            {line.substring(3)}
          </h2>
        );
      } else if (line.startsWith('# ')) {
        return (
          <h1 key={index} className="mb-3 mt-4 break-words text-2xl font-bold text-white" style={{ fontSize: '1.5rem' }}>
            {line.substring(2)}
          </h1>
        );
      } else if (line.trim() === '') {
        return <br key={index} />;
      } else {
        // Handle bold text and regular content
        const parts = line.split(/(\*\*.*?\*\*)/g);
        const formattedParts = parts.map((part, partIndex) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return (
              <strong key={partIndex} className="font-bold">
                {part.slice(2, -2)}
              </strong>
            );
          }
          return part;
        });
        
        return (
          <p key={index} className="mb-2 break-words leading-relaxed text-gray-100" style={{ fontSize: '1rem' }}>
            {formattedParts}
          </p>
        );
      }
    });
    
    return <div className="space-y-1">{formattedLines}</div>;
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4"
      onClick={onClose}
    >
      {/* Backdrop with blur */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md" />
      
      {/* Chatbot Container */}
      <div 
        className="animate-form-entrance relative my-auto h-[95vh] w-full max-w-2xl md:h-[80vh] md:max-h-[600px]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Animated Border Container */}
        <div className="absolute inset-[-3px] overflow-hidden rounded-2xl">
          {/* Base gradient background */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20" />
          
          {/* Saber Energy Border */}
          <svg className="absolute inset-0 size-full">
            <defs>
              {/* Energy gradient that shifts colors */}
              <linearGradient id="chatEnergyBeam" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#3B82F6" stopOpacity="0" />
                <stop offset="10%" stopColor="#3B82F6" stopOpacity="0.3" />
                <stop offset="30%" stopColor="#8B5CF6" stopOpacity="0.6" />
                <stop offset="50%" stopColor="#EC4899" stopOpacity="1">
                  <animate attributeName="stop-color" 
                    values="#EC4899;#F59E0B;#EC4899" 
                    dur="2s" 
                    repeatCount="indefinite" />
                </stop>
                <stop offset="70%" stopColor="#8B5CF6" stopOpacity="0.6" />
                <stop offset="90%" stopColor="#3B82F6" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
              </linearGradient>
              
              {/* Intense glow filter */}
              <filter id="chatSaberGlow">
                <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                <feGaussianBlur stdDeviation="6" result="coloredBlur2"/>
                <feMerge>
                  <feMergeNode in="coloredBlur2"/>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            
            {/* Outer glow layer */}
            <rect
              x="1"
              y="1"
              width="99%"
              height="99%"
              rx="16"
              fill="none"
              stroke="url(#chatEnergyBeam)"
              strokeWidth="6"
              strokeDasharray="120 280"
              filter="url(#chatSaberGlow)"
              className="animate-saber-beam"
              opacity="0.6"
            />
            
            {/* Main energy beam */}
            <rect
              x="2"
              y="2"
              width="98%"
              height="98%"
              rx="15"
              fill="none"
              stroke="url(#chatEnergyBeam)"
              strokeWidth="3"
              strokeDasharray="100 300"
              filter="url(#chatSaberGlow)"
              className="animate-saber-beam"
            />
            
            {/* Inner bright core */}
            <rect
              x="3"
              y="3"
              width="97%"
              height="97%"
              rx="14"
              fill="none"
              stroke="white"
              strokeWidth="1"
              strokeDasharray="60 340"
              className="animate-saber-beam"
              opacity="0.9"
            />
          </svg>
        </div>

        {/* Chat Content */}
        <div className="relative flex h-full flex-col rounded-2xl bg-gray-900/95 shadow-2xl backdrop-blur-xl">
          {/* Header - Responsive layout */}
          <div className="flex items-center justify-between border-b border-gray-700/50 p-4 md:p-6">
            <div className="flex items-center space-x-3">
              <div className="flex size-10 items-center justify-center rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500">
                <Bot className="size-6 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold md:text-xl">
                  <span className="animate-gradient bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                    AI Assistant
                  </span>
                </h2>
                <p className="text-xs text-gray-400 md:text-sm">Powered by LightRAG</p>
              </div>
            </div>
            
            {/* Mode Toggle and Close button - centered on mobile */}
            <div className="flex items-center gap-2 md:gap-4">
              {/* Mode selector - no "Mode:" text on mobile */}
              <div className="flex items-center gap-2">
                <span className="hidden text-sm text-gray-400 md:inline">Mode:</span>
                <div className="flex flex-col gap-1 rounded-lg bg-gray-800 p-1 md:flex-row md:gap-0">
                  <button
                    onClick={() => setQueryMode('naive')}
                    className={`flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium transition-all duration-200 md:px-2 md:py-1 ${
                      queryMode === 'naive'
                        ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    <Zap className="size-3" />
                    <span>Fast</span>
                  </button>
                  <button
                    onClick={() => setQueryMode('global')}
                    className={`flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium transition-all duration-200 md:px-2 md:py-1 ${
                      queryMode === 'global'
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    <Clock className="size-3" />
                    <span>Deep</span>
                  </button>
                  <button
                    onClick={() => setQueryMode('hybrid')}
                    className={`flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium transition-all duration-200 md:px-2 md:py-1 ${
                      queryMode === 'hybrid'
                        ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    <Brain className="size-3" />
                    <span>Max</span>
                  </button>
                </div>
              </div>
              
              <button
                onClick={clearChat}
                className="hidden rounded-lg px-3 py-1 text-gray-400 transition-colors duration-200 hover:bg-gray-800 hover:text-white md:block"
              >
                Clear
              </button>
              
              {/* Close button */}
              <button
                onClick={onClose}
                className="rounded-lg bg-gray-800 p-2 text-gray-400 transition-colors duration-200 hover:bg-gray-700 hover:text-white"
                aria-label="Close chat"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 space-y-4 overflow-y-auto p-6">
            {messages.map((message, index) => {
              // Check if this is the latest bot message
              const isLatestBotMessage = message.isBot && index === messages.length - 1;
              
              return (
                <div
                  key={message.id}
                  ref={isLatestBotMessage ? latestBotMessageRef : null}
                  className={`flex ${message.isBot ? 'justify-start' : 'justify-end'}`}
                >
              
                <div className={`flex max-w-[80%] items-start space-x-3 ${message.isBot ? '' : 'flex-row-reverse space-x-reverse'}`}>
                  {/* Avatar */}
                  <div className={`flex size-8 shrink-0 items-center justify-center rounded-full ${
                    message.isBot 
                      ? 'bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500' 
                      : 'bg-gray-700'
                  }`}>
                    {message.isBot ? (
                      <Bot className="size-4 text-white" />
                    ) : (
                      <User className="size-4 text-white" />
                    )}
                  </div>
                  
                  {/* Message Bubble */}
                  <div className={`overflow-hidden break-words rounded-2xl px-4 py-3 ${
                    message.isBot 
                      ? 'bg-gray-800/70 text-gray-100' 
                      : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                  }`}>
                    <div className="overflow-hidden text-sm leading-relaxed">
                      {message.isBot ? formatMarkdownContent(message.text) : (
                        <p className="whitespace-pre-wrap break-words">{message.text}</p>
                      )}
                    </div>
                    <p className="mt-2 text-xs opacity-60">
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
                </div>
              );
            })}
            
            {/* Loading indicator */}
            {isLoading && (
              <div className="flex justify-start">
                <div className="flex max-w-[80%] items-start space-x-3">
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500">
                    <Bot className="size-4 text-white" />
                  </div>
                  <div className="rounded-2xl bg-gray-800/70 px-4 py-3">
                    <div className="flex space-x-1">
                      <div className="size-2 animate-bounce rounded-full bg-gray-400" style={{ animationDelay: '0ms' }}></div>
                      <div className="size-2 animate-bounce rounded-full bg-gray-400" style={{ animationDelay: '150ms' }}></div>
                      <div className="size-2 animate-bounce rounded-full bg-gray-400" style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input - Responsive layout */}
          <div className="border-t border-gray-700/50 p-4 md:p-6">
            <div className="flex flex-col space-y-3 md:flex-row md:space-x-3 md:space-y-0">
              <textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me anything about Jason..."
                className="max-h-32 min-h-[80px] w-full resize-none rounded-lg border border-gray-700 bg-gray-800/50 px-4 py-3 text-white transition-all duration-200 placeholder:text-gray-500 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-400/20 md:min-h-[44px] md:flex-1"
                rows={3}
                disabled={isLoading}
              />
              <button
                onClick={sendMessage}
                disabled={isLoading || !inputValue.trim()}
                className="group relative mx-auto h-12 w-1/2 overflow-hidden rounded-lg disabled:cursor-not-allowed disabled:opacity-50 md:mx-0 md:size-auto md:px-4 md:py-3"
              >
                <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 transition-transform duration-300 group-hover:scale-105 group-disabled:scale-100" />
                <div className="relative flex h-full items-center justify-center rounded-md bg-gray-900 px-4 py-2 transition-all duration-300 group-hover:bg-transparent group-disabled:bg-gray-900 md:m-0.5 md:px-2 md:py-1">
                  <Send className="mr-2 size-5 text-white md:mr-0" />
                  <span className="text-white md:hidden">Send</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIChatbot;